import { EditorState, EditorSelection, Extension } from '@codemirror/state';
import { EditorView, keymap, lineNumbers, highlightActiveLine, highlightActiveLineGutter, KeyBinding } from '@codemirror/view';
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands';
import { markdown } from '@codemirror/lang-markdown';
import { javascript } from '@codemirror/lang-javascript';
import { php } from '@codemirror/lang-php';
import { python } from '@codemirror/lang-python';
import { json } from '@codemirror/lang-json';
import { searchKeymap, highlightSelectionMatches, openSearchPanel } from '@codemirror/search';
import { AUTOSAVE_DELAY_MS, INDENT_UNIT } from '../config/settings';
import { imageService } from '../services/images';
import { formatCodeBlock, formatFile, isFormatSupported } from '../services/formatter';

export type FileType = 'markdown' | 'javascript' | 'typescript' | 'php' | 'python' | 'json' | 'text';

export type EditorChangeCallback = (content: string) => void;
export type CursorChangeCallback = (line: number) => void;

// Función helper para envolver texto seleccionado
function wrapSelection(view: EditorView, before: string, after: string): boolean {
  const { from, to } = view.state.selection.main;
  const selectedText = view.state.sliceDoc(from, to);

  view.dispatch({
    changes: { from, to, insert: before + selectedText + after },
    selection: EditorSelection.cursor(from + before.length + selectedText.length + after.length)
  });

  return true;
}

// Atajos de teclado para Markdown
const markdownKeymap: KeyBinding[] = [
  {
    key: 'Mod-b',
    run: (view) => wrapSelection(view, '**', '**') // Bold
  },
  {
    key: 'Mod-i',
    run: (view) => wrapSelection(view, '*', '*') // Italic
  },
  {
    key: 'Mod-k',
    run: (view) => {
      const { from, to } = view.state.selection.main;
      const selectedText = view.state.sliceDoc(from, to);
      const linkText = selectedText || 'texto';
      view.dispatch({
        changes: { from, to, insert: `[${linkText}](url)` },
        selection: EditorSelection.range(from + linkText.length + 3, from + linkText.length + 6)
      });
      return true;
    }
  },
  {
    key: 'Mod-`',
    run: (view) => wrapSelection(view, '`', '`') // Inline code
  },
  {
    key: 'Mod-Shift-k',
    run: (view) => {
      const { from, to } = view.state.selection.main;
      const selectedText = view.state.sliceDoc(from, to);
      view.dispatch({
        changes: { from, to, insert: '```\n' + selectedText + '\n```' },
        selection: EditorSelection.cursor(from + 4)
      });
      return true;
    }
  },
  {
    key: 'Mod-Shift-x',
    run: (view) => wrapSelection(view, '~~', '~~') // Strikethrough
  },
  {
    key: 'Mod-l',
    run: (view) => {
      const { from } = view.state.selection.main;
      const line = view.state.doc.lineAt(from);
      // Insertar al inicio de la línea
      view.dispatch({
        changes: { from: line.from, insert: '- ' },
        selection: EditorSelection.cursor(from + 2)
      });
      return true;
    }
  },
  {
    key: 'Mod-Shift-c',
    run: (view) => {
      const { from } = view.state.selection.main;
      const line = view.state.doc.lineAt(from);
      view.dispatch({
        changes: { from: line.from, insert: '- [ ] ' },
        selection: EditorSelection.cursor(from + 6)
      });
      return true;
    }
  }
  // Tab, Shift-Tab, and JSON beautify are now in getKeymapForFileType()
];

const darkTheme = EditorView.theme({
  '&': {
    backgroundColor: '#1a1a2e',
    color: '#eee'
  },
  '.cm-content': {
    caretColor: '#e94560'
  },
  '.cm-cursor': {
    borderLeftColor: '#e94560'
  },
  '.cm-activeLine': {
    backgroundColor: '#16213e'
  },
  '.cm-activeLineGutter': {
    backgroundColor: '#16213e'
  },
  '.cm-gutters': {
    backgroundColor: '#0f0f23',
    color: '#666',
    border: 'none'
  },
  '.cm-selectionBackground': {
    backgroundColor: '#e9456040'
  },
  '&.cm-focused .cm-selectionBackground': {
    backgroundColor: '#e9456060'
  },
  '.cm-line': {
    padding: '0 4px'
  }
}, { dark: true });

export class Editor {
  private view: EditorView | null = null;
  private container: HTMLElement;
  private onChange: EditorChangeCallback | null = null;
  private onCursorChange: CursorChangeCallback | null = null;
  private saveTimeout: number | null = null;
  private currentDocId: string | null = null;
  private currentFileType: FileType = 'markdown';

  constructor(containerId: string) {
    const container = document.getElementById(containerId);
    if (!container) {
      throw new Error(`Container ${containerId} not found`);
    }
    this.container = container;
  }

  init(content: string = '', docId: string | null = null, fileType: FileType = 'markdown'): void {
    this.currentDocId = docId;
    this.currentFileType = fileType;

    if (this.view) {
      this.view.destroy();
    }

    const extensions = [
      lineNumbers(),
      highlightActiveLine(),
      highlightActiveLineGutter(),
      highlightSelectionMatches(),
      history(),
      this.getLanguageExtension(fileType),
      darkTheme,
      keymap.of([
        ...this.getKeymapForFileType(fileType),
        ...defaultKeymap,
        ...historyKeymap,
        ...searchKeymap
      ]),
      EditorView.lineWrapping,
      EditorView.updateListener.of((update) => {
        if (update.docChanged && this.onChange) {
          this.scheduleAutosave();
        }
        // Notificar cambio de cursor para sync scroll
        if (update.selectionSet && this.onCursorChange) {
          const pos = update.state.selection.main.head;
          const line = update.state.doc.lineAt(pos);
          this.onCursorChange(line.number);
        }
      }),
      // Manejo de paste (imágenes) y click (tasks)
      EditorView.domEventHandlers({
        click: (event, view) => {
          const target = event.target as HTMLElement;

          // Detect clicks on task checkboxes (rendered by markdown)
          // CodeMirror renders tasks as text, so we check if click is near task syntax
          const pos = view.posAtCoords({ x: event.clientX, y: event.clientY });
          if (pos === null) return false;

          const line = view.state.doc.lineAt(pos);
          const lineText = line.text;

          // Check if line is a task: - [ ] or - [x]
          const taskMatch = lineText.match(/^(\s*)-\s*\[([ xX])\]/);
          if (!taskMatch) return false;

          // Check if click is within checkbox area (first 10 chars approximately)
          const clickOffset = pos - line.from;
          if (clickOffset > 10) return false;

          // Toggle task via API
          if (this.currentDocId) {
            import('../services/api').then(({ api }) => {
              api.toggleTask(this.currentDocId!, line.number).then(response => {
                if (response.success) {
                  // Reload content to show updated checkbox
                  const newContent = view.state.doc.toString();
                  const lines = newContent.split('\n');
                  const lineIndex = line.number - 1;

                  // Toggle checkbox character in place
                  const oldCheckmark = taskMatch[2];
                  const newCheckmark = oldCheckmark === ' ' ? 'x' : ' ';
                  lines[lineIndex] = lineText.replace(/\[([ xX])\]/, `[${newCheckmark}]`);

                  // Update view
                  view.dispatch({
                    changes: { from: 0, to: view.state.doc.length, insert: lines.join('\n') },
                    selection: EditorSelection.cursor(pos)
                  });
                }
              });
            });
          }

          return true;
        },
        paste: (event, view) => {
          const clipboardData = event.clipboardData;
          if (!clipboardData) return false;

          // Detectar imagen en portapapeles
          if (imageService.hasImageInClipboard(clipboardData)) {
            event.preventDefault();
            const imageItem = imageService.getImageFromClipboard(clipboardData);
            if (imageItem) {
              const { from, to } = view.state.selection.main;

              // Insertar placeholder mientras sube
              const placeholder = '![Subiendo imagen...]()';
              view.dispatch({
                changes: { from, to, insert: placeholder },
                selection: EditorSelection.cursor(from + placeholder.length)
              });

              // Subir imagen
              imageService.uploadFromClipboard(imageItem).then((result) => {
                if (result) {
                  // Reemplazar placeholder con imagen real
                  const doc = view.state.doc.toString();
                  const placeholderPos = doc.indexOf(placeholder);
                  if (placeholderPos >= 0) {
                    const imageMarkdown = `![imagen](${result.url})`;
                    view.dispatch({
                      changes: {
                        from: placeholderPos,
                        to: placeholderPos + placeholder.length,
                        insert: imageMarkdown
                      }
                    });
                  }
                } else {
                  // Error: quitar placeholder
                  const doc = view.state.doc.toString();
                  const placeholderPos = doc.indexOf(placeholder);
                  if (placeholderPos >= 0) {
                    view.dispatch({
                      changes: {
                        from: placeholderPos,
                        to: placeholderPos + placeholder.length,
                        insert: '![Error al subir imagen]()'
                      }
                    });
                  }
                }
              });
            }
            return true;
          }

          return false;
        },
        drop: (event, view) => {
          const dataTransfer = event.dataTransfer;
          if (!dataTransfer) return false;

          // Verificar si hay archivos de imagen
          const files = Array.from(dataTransfer.files);
          const imageFile = files.find(f => f.type.startsWith('image/'));

          if (imageFile) {
            event.preventDefault();

            // Obtener posición del drop
            const pos = view.posAtCoords({ x: event.clientX, y: event.clientY });
            if (pos === null) return false;

            // Insertar placeholder
            const placeholder = '![Subiendo imagen...]()';
            view.dispatch({
              changes: { from: pos, insert: placeholder },
              selection: EditorSelection.cursor(pos + placeholder.length)
            });

            // Subir imagen
            imageService.uploadImage(imageFile).then((result) => {
              const doc = view.state.doc.toString();
              const placeholderPos = doc.indexOf(placeholder);
              if (placeholderPos >= 0) {
                const imageMarkdown = result
                  ? `![${imageFile.name}](${result.url})`
                  : '![Error al subir imagen]()';
                view.dispatch({
                  changes: {
                    from: placeholderPos,
                    to: placeholderPos + placeholder.length,
                    insert: imageMarkdown
                  }
                });
              }
            });

            return true;
          }

          return false;
        }
      })
    ];

    const state = EditorState.create({
      doc: content,
      extensions
    });

    this.view = new EditorView({
      state,
      parent: this.container
    });

    // Click en área vacía para extender documento
    this.setupEmptyAreaClick();
  }

  private setupEmptyAreaClick(): void {
    if (!this.view) return;

    const scroller = this.container.querySelector('.cm-scroller');
    if (!scroller) return;

    scroller.addEventListener('click', (e: Event) => {
      const mouseEvent = e as MouseEvent;
      if (!this.view) return;

      // Obtener posición del click relativa al contenido
      const contentEl = this.container.querySelector('.cm-content');
      if (!contentEl) return;

      const contentRect = contentEl.getBoundingClientRect();
      const clickY = mouseEvent.clientY;

      // Si el click está debajo del contenido real
      if (clickY > contentRect.bottom) {
        // Calcular cuántas líneas necesitamos añadir
        const lineHeight = 20; // altura aproximada de línea
        const extraLines = Math.ceil((clickY - contentRect.bottom) / lineHeight);

        if (extraLines > 0) {
          const doc = this.view.state.doc;
          const endPos = doc.length;

          // Añadir líneas vacías
          const newlines = '\n'.repeat(extraLines);
          const transaction = this.view.state.update({
            changes: { from: endPos, insert: newlines },
            selection: { anchor: endPos + extraLines }
          });

          this.view.dispatch(transaction);
          this.view.focus();
        }
      }
    });
  }

  private scheduleAutosave(): void {
    if (this.saveTimeout) {
      window.clearTimeout(this.saveTimeout);
    }

    this.saveTimeout = window.setTimeout(() => {
      if (this.onChange) {
        this.onChange(this.getContent());
      }
    }, AUTOSAVE_DELAY_MS);
  }

  setContent(content: string): void {
    if (!this.view) return;

    const transaction = this.view.state.update({
      changes: {
        from: 0,
        to: this.view.state.doc.length,
        insert: content
      }
    });

    this.view.dispatch(transaction);
  }

  setReadOnly(readOnly: boolean): void {
    if (!this.view) return;

    // Simple approach: disable/enable the editor element
    const dom = this.view.dom;
    if (readOnly) {
      dom.classList.add('editor-readonly');
      this.view.contentDOM.contentEditable = 'false';
    } else {
      dom.classList.remove('editor-readonly');
      this.view.contentDOM.contentEditable = 'true';
    }
  }

  getContent(): string {
    if (!this.view) return '';
    return this.view.state.doc.toString();
  }

  onContentChange(callback: EditorChangeCallback): void {
    this.onChange = callback;
  }

  onCursorChangeCallback(callback: CursorChangeCallback): void {
    this.onCursorChange = callback;
  }

  focus(): void {
    this.view?.focus();
  }

  destroy(): void {
    if (this.saveTimeout) {
      window.clearTimeout(this.saveTimeout);
    }
    this.view?.destroy();
    this.view = null;
  }

  getDocId(): string | null {
    return this.currentDocId;
  }

  setDocId(id: string): void {
    this.currentDocId = id;
  }

  // Obtener información del cursor
  getCursorInfo(): { line: number; column: number } {
    if (!this.view) return { line: 1, column: 1 };

    const pos = this.view.state.selection.main.head;
    const line = this.view.state.doc.lineAt(pos);

    return {
      line: line.number,
      column: pos - line.from + 1
    };
  }

  getWordCount(): number {
    if (!this.view) return 0;
    const text = this.view.state.doc.toString();
    return text.trim() ? text.trim().split(/\s+/).length : 0;
  }

  // Abrir panel de búsqueda (Ctrl+F ya funciona, esto es para botón)
  openSearch(): void {
    if (this.view) {
      openSearchPanel(this.view);
    }
  }

  // Obtener la vista para uso externo (preview sync)
  getView(): EditorView | null {
    return this.view;
  }

  // Ir a una línea específica
  scrollToLine(lineNumber: number): void {
    if (!this.view) return;

    const doc = this.view.state.doc;
    if (lineNumber < 1 || lineNumber > doc.lines) return;

    const line = doc.line(lineNumber);

    this.view.dispatch({
      selection: { anchor: line.from },
      effects: EditorView.scrollIntoView(line.from, { y: 'start' })
    });

    this.view.focus();
  }

  // Obtener total de líneas
  getLineCount(): number {
    if (!this.view) return 0;
    return this.view.state.doc.lines;
  }

  // Obtener tipo de archivo actual
  getFileType(): FileType {
    return this.currentFileType;
  }

  // Obtener extensión de lenguaje según tipo de archivo
  private getLanguageExtension(fileType: FileType): Extension {
    switch (fileType) {
      case 'javascript':
      case 'typescript':
        return javascript({ typescript: fileType === 'typescript', jsx: true });
      case 'php':
        return php();
      case 'python':
        return python();
      case 'json':
        return json();
      case 'text':
        return []; // Sin resaltado de sintaxis
      case 'markdown':
      default:
        return markdown();
    }
  }

  // Obtener keymap según tipo de archivo
  private getKeymapForFileType(fileType: FileType): KeyBinding[] {
    // Keybindings comunes para todos los tipos (Tab indent, JSON beautify)
    const commonKeymap: KeyBinding[] = [
      // Tab - Indent (4 spaces)
      {
        key: 'Tab',
        run: (view) => {
          const { from, to } = view.state.selection.main;
          const doc = view.state.doc;

          if (from !== to) {
            const startLine = doc.lineAt(from).number;
            const endLine = doc.lineAt(to).number;
            const changes: { from: number; insert: string }[] = [];
            for (let i = startLine; i <= endLine; i++) {
              const line = doc.line(i);
              changes.push({ from: line.from, insert: INDENT_UNIT });
            }
            view.dispatch({ changes });
          } else {
            view.dispatch({
              changes: { from, insert: INDENT_UNIT },
              selection: EditorSelection.cursor(from + INDENT_UNIT.length)
            });
          }
          return true;
        },
        preventDefault: true
      },
      // Shift+Tab - Dedent
      {
        key: 'Shift-Tab',
        run: (view) => {
          const { from, to } = view.state.selection.main;
          const doc = view.state.doc;
          const startLine = doc.lineAt(from).number;
          const endLine = doc.lineAt(to).number;

          const changes: { from: number; to: number }[] = [];
          for (let i = startLine; i <= endLine; i++) {
            const line = doc.line(i);
            const lineText = line.text;
            let spacesToRemove = 0;
            for (let j = 0; j < INDENT_UNIT.length && j < lineText.length; j++) {
              if (lineText[j] === ' ') spacesToRemove++;
              else break;
            }
            if (spacesToRemove > 0) {
              changes.push({ from: line.from, to: line.from + spacesToRemove });
            }
          }
          if (changes.length > 0) {
            view.dispatch({ changes });
          }
          return true;
        },
        preventDefault: true
      },
      // Ctrl+Shift+J - Beautify JSON
      {
        key: 'Mod-Shift-j',
        run: (view) => {
          const { from, to } = view.state.selection.main;
          let textToFormat: string;
          let replaceFrom: number;
          let replaceTo: number;

          if (from !== to) {
            textToFormat = view.state.sliceDoc(from, to);
            replaceFrom = from;
            replaceTo = to;
          } else {
            textToFormat = view.state.doc.toString();
            replaceFrom = 0;
            replaceTo = view.state.doc.length;
          }

          try {
            const parsed = JSON.parse(textToFormat);
            const formatted = JSON.stringify(parsed, null, 2);
            view.dispatch({
              changes: { from: replaceFrom, to: replaceTo, insert: formatted },
              selection: EditorSelection.cursor(replaceFrom + formatted.length)
            });
          } catch (e) {
            const statusEl = document.getElementById('status-bar');
            if (statusEl) {
              const originalText = statusEl.textContent;
              statusEl.textContent = 'Error: JSON invalido';
              statusEl.style.color = '#e94560';
              setTimeout(() => {
                statusEl.textContent = originalText || '';
                statusEl.style.color = '';
              }, 3000);
            }
          }
          return true;
        }
      },
      // Ctrl+Shift+F - Format code (Prettier)
      {
        key: 'Mod-Shift-f',
        run: (view) => {
          const currentFileType = this.currentFileType;
          const cursorPos = view.state.selection.main.head;
          const content = view.state.doc.toString();

          const showStatus = (message: string, isError: boolean = false) => {
            const statusEl = document.getElementById('status-bar');
            if (statusEl) {
              const originalText = statusEl.textContent;
              statusEl.textContent = message;
              statusEl.style.color = isError ? '#e94560' : '#4ade80';
              setTimeout(() => {
                statusEl.textContent = originalText || '';
                statusEl.style.color = '';
              }, 3000);
            }
          };

          // For markdown files, try to format code block at cursor
          if (currentFileType === 'markdown') {
            formatCodeBlock(content, cursorPos).then((result) => {
              if (result.success && result.formatted !== undefined && result.from !== undefined && result.to !== undefined) {
                view.dispatch({
                  changes: { from: result.from, to: result.to, insert: result.formatted }
                });
                showStatus('Codigo formateado');
              } else {
                showStatus(result.error || 'No se pudo formatear', true);
              }
            });
          } else if (isFormatSupported(currentFileType)) {
            // For supported standalone files, format entire content
            formatFile(content, currentFileType).then((result) => {
              if (result.success && result.formatted) {
                view.dispatch({
                  changes: { from: 0, to: view.state.doc.length, insert: result.formatted }
                });
                showStatus('Archivo formateado');
              } else {
                showStatus(result.error || 'No se pudo formatear', true);
              }
            });
          } else {
            showStatus(`Formato no soportado para ${currentFileType}`, true);
          }

          return true;
        }
      }
    ];

    // Para markdown, añadir keybindings específicos de markdown
    if (fileType === 'markdown') {
      return [...markdownKeymap, ...commonKeymap];
    }

    // Para otros tipos, solo los comunes
    return commonKeymap;
  }
}
